import { Badge, Chip } from './ui';

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export const ENGAGEMENT_LABEL: Record<string, string> = { active: 'Active', idle: 'Idle', silent: 'Silent' };
export const WELLBEING_LABEL: Record<string, string> = { ok: 'OK', watch: 'Watch', concern: 'Concern' };
/** Label for a student with no wellbeing check-in on record (design `WELL.none`). */
export const NO_CHECKINS_LABEL = 'No check-ins';

export function classLabel(
  gradeName: string | null | undefined,
  section: { display?: string | null; name?: string | null } | null | undefined,
): string {
  if (!section) return `${gradeName ?? '—'} · No section`;
  const label = section.display ?? section.name ?? '';
  const g = (gradeName ?? '').trim().toLowerCase();
  if (g && label.trim().toLowerCase().startsWith(g)) return label;
  return `${gradeName ?? '—'} · ${label}`;
}

export const fullNameOf = (r: any): string => `${r.first_name} ${r.last_name}`.trim() || r.username;
export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
};
export function Initials({ name, large = false, hi = false }: { name: string; large?: boolean; hi?: boolean }) {
  return (
    <span aria-hidden="true" className={`grid flex-none place-items-center font-semibold ${hi ? 'bg-accent-bg text-accent-ink' : 'bg-track text-muted-2'} ${large ? 'h-12 w-12 rounded-[15px] text-[15px]' : 'h-9 w-9 rounded-[12px] text-xs'}`}>
      {initialsOf(name)}
    </span>
  );
}

const BAND_TITLE: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' };

/** Design `.pill p-crit|p-warn|p-ok|p-none`: "82 High", or "No data" when there is no score. */
export function RiskPill({ r }: { r: any }) {
  if (r.risk_band === 'insufficient_data' || (!r.risk_band && r.risk_score == null)) return <Badge tone="neutral" size="sm">No data</Badge>;
  if (!r.risk_band) return <Badge tone="neutral" size="sm">{r.risk_score.toFixed(0)}</Badge>;
  const tone = r.risk_band === 'high' ? 'danger' : r.risk_band === 'medium' ? 'warning' : 'success';
  const band = BAND_TITLE[r.risk_band] ?? r.risk_band;
  return <Badge tone={tone} size="sm">{r.risk_score != null ? `${r.risk_score.toFixed(0)} ${band}` : band}</Badge>;
}

/**
 * Design alerts cell. Table: a red pill with just the number, or a muted mono 0.
 * `long` (cards): a chip reading "3 open alerts", or a neutral "No alerts" chip.
 */
export function AlertsChip({ count, long = false }: { count: number; long?: boolean }) {
  if (long) {
    return count > 0
      ? <Chip tone="down">{count} open {count === 1 ? 'alert' : 'alerts'}</Chip>
      : <Chip tone="neutral">No alerts</Chip>;
  }
  return count > 0
    ? <Badge tone="danger" size="sm" className="font-mono font-medium">{count}</Badge>
    : <span className="font-mono font-medium text-muted">0</span>;
}
